import { ref } from "vue";
import { defaultApi, type IResponse } from "@/api";
import { logError, logInfo, logEvent, logMeasurement, FEATURE_NAMES, METRIC_TYPE } from "@/loggers";

export const useWidgetOrchestrator = () => {
  const loading = ref(false);
  const error = ref<string | null>(null);

  const fetchWidgetData = async (dashboardId: string, payload: any): Promise<IResponse> => {
    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Fetching widget data for dashboard ${dashboardId}`);

    try {
      loading.value = true;
      error.value = null;

      const response = await defaultApi.apiVersion1.widgetOrchestrator.post<IResponse>(
        `dashboard/${dashboardId}/widgets/data`,
        payload
      );

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Widget data fetched successfully for dashboard ${dashboardId}`);

      return response;
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to fetch widget data";
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "orchestrator/widgets/data",
        error: errorMessage,
        statusCode: err?.response?.status
      });

      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error fetching widget data: ${errorMessage}`);

      throw err;
    } finally {
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "orchestrator/widgets/data"
      });
      loading.value = false;
    }
  };

  const getWidgetHyperlink = async (payload) => {
    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Fetching hyperlink for widgetId ${payload.widgetId}`);

    try {
      const response = await defaultApi.apiVersion1.widgetOrchestrator.post<IResponse>("hyperlink", payload);

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Hyperlink fetched successfully for widgetId ${payload.widgetId}`);

      return response.url;
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to fetch widget hyperlink";

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "orchestrator/widget/hyperlink",
        widgetId: payload.widgetId,
        payload: JSON.stringify(payload),
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error fetching widget hyperlink: ${errorMessage}`);
      throw err;
    } finally {
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "orchestrator/widget/hyperlink",
        widgetId: payload.widgetId,
        payload: JSON.stringify(payload)
      });
    }
  };

  return {
    loading,
    error,
    fetchWidgetData,
    getWidgetHyperlink
  };
};
